import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Sliders, Check } from "lucide-react";

const ACCENT = "#d04260";

/**
 * Granular GDPR cookie banner (Reg. UE 2016/679 & D.Lgs. 196/2003).
 *
 * Storage: ``cookie_consent`` in localStorage is a JSON string:
 *   { essential: true, analytics: bool, marketing: bool, at: ISO }
 * For back-compat with legacy listeners we also emit a coarse ``level``:
 *   - "all"        → analytics && marketing
 *   - "essential"  → analytics && !marketing (or all off)
 * Listeners on ``hostland-consent`` receive both:
 *   { level, categories: { essential, analytics, marketing } }
 *
 * Until a choice is made, blocks the page chrome so non-essential trackers
 * (OneSignal, analytics) can gate themselves on the event.
 */

const CONSENT_KEY = "cookie_consent";

const parseSaved = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    // Legacy strings: "all" | "essential"
    if (raw === "all") return { essential: true, analytics: true, marketing: true };
    if (raw === "essential") return { essential: true, analytics: false, marketing: false };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return { essential: true, analytics: !!parsed.analytics, marketing: !!parsed.marketing };
    return null;
  } catch {
    return null;
  }
};

const coarseLevel = (c) => (c.analytics && c.marketing ? "all" : "essential");

export const getCookieConsent = () => {
  const c = parseSaved();
  return c ? coarseLevel(c) : null;
};

export const hasMarketingConsent = () => {
  const c = parseSaved();
  return !!(c && c.marketing);
};

export const hasAnalyticsConsent = () => {
  const c = parseSaved();
  return !!(c && c.analytics);
};

const CookieBanner = () => {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // MAI mostrare il banner ai crawler/renderer headless: non installiamo
    // cookie per i bot e l'overlay con blur a schermo intero degraderebbe la
    // resa della pagina per Googlebot (rischio "Scansionata ma non indicizzata").
    const BOT_RE = /bot|crawler|spider|slurp|googlebot|bingbot|yandex|baiduspider|duckduckbot|applebot|facebookexternalhit|linkedinbot|twitterbot|whatsapp|telegrambot|prerender|rendertron|headless|lighthouse|pagespeed|gptbot|claudebot|anthropic|perplexitybot|amazonbot|ccbot|bytespider/i;
    try {
      if (navigator.webdriver || BOT_RE.test(navigator.userAgent)) return;
    } catch {}
    // Skip when we're rendered inside an iframe (e.g. the demo mockups on the
    // Hostland landing page). Showing a consent modal inside a demo mockup is
    // a bad UX and it is legally handled by the parent page anyway.
    try {
      if (window.self !== window.top) return;
    } catch { return; /* cross-origin frame throws → still an embed */ }
    // Explicit opt-out via query flag (used by iframe embeds and screenshots).
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("embed") === "1" || q.get("nocookie") === "1") return;
    } catch {}
    const saved = parseSaved();
    if (!saved) {
      setShow(true);
    } else {
      // Re-broadcast on mount so late-loading components catch the saved choice.
      window.dispatchEvent(new CustomEvent("hostland-consent", {
        detail: { level: coarseLevel(saved), categories: saved },
      }));
    }
  }, []);

  const persist = (categories) => {
    const record = { ...categories, at: new Date().toISOString() };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    } catch {}
    window.dispatchEvent(new CustomEvent("hostland-consent", {
      detail: { level: coarseLevel(categories), categories },
    }));
    setShow(false);
  };

  const acceptAll = () => persist({ essential: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ essential: true, analytics: false, marketing: false });
  const saveGranular = () => persist({ essential: true, analytics, marketing });

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Blocking backdrop — consent-first: user MUST make a choice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99] bg-black/40 backdrop-blur-sm"
            data-testid="cookie-banner-backdrop"
          />
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[100] p-4"
            data-testid="cookie-banner"
          >
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl shadow-black/10 border border-zinc-100 p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${ACCENT}10` }}>
                  <Shield className="w-4 h-4" style={{ color: ACCENT }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-zinc-900 mb-1">Rispettiamo la tua privacy</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Ai sensi del <strong>Regolamento UE 2016/679 (GDPR)</strong> e del D.Lgs. 196/2003,
                    Hostland usa cookie tecnici essenziali per far funzionare il sito (sessione,
                    login, sicurezza). Per attivare analytics anonimi e notifiche push serve il tuo
                    <strong> consenso esplicito e granulare</strong>. Nessun cookie non essenziale
                    viene installato finché non scegli.{" "}
                    <a href="/cookie-policy" className="underline font-medium" style={{ color: ACCENT }}>Cookie Policy</a>{" "}·{" "}
                    <a href="/privacy" className="underline font-medium" style={{ color: ACCENT }}>Privacy Policy</a>.
                  </p>
                </div>
              </div>

              {showDetails && (
                <div className="mt-4 space-y-2" data-testid="cookie-granular-panel">
                  {/* Essenziali — sempre attivi */}
                  <div className="flex items-start gap-3 p-3 rounded-xl border border-zinc-100 bg-zinc-50/70">
                    <div className="mt-0.5 w-9 h-5 rounded-full bg-zinc-300 flex items-center px-0.5">
                      <div className="w-4 h-4 rounded-full bg-white shadow ml-auto" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-bold text-zinc-900">Essenziali</p>
                        <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">SEMPRE ATTIVI</span>
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Sessione utente, login, protezione CSRF. Senza questi il sito non funziona.</p>
                    </div>
                  </div>

                  {/* Analytics */}
                  <button
                    type="button"
                    onClick={() => setAnalytics((v) => !v)}
                    className="w-full flex items-start gap-3 p-3 rounded-xl border border-zinc-100 hover:border-zinc-200 text-left transition-colors"
                    data-testid="cookie-toggle-analytics"
                  >
                    <div className={`mt-0.5 w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${analytics ? "" : "bg-zinc-200"}`}
                         style={{ background: analytics ? ACCENT : undefined }}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-all ${analytics ? "ml-auto" : ""}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-zinc-900">Analytics anonimi</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Ci aiuti a capire come viene usata la piattaforma (pageview aggregati, senza profilazione).</p>
                    </div>
                  </button>

                  {/* Marketing / Push */}
                  <button
                    type="button"
                    onClick={() => setMarketing((v) => !v)}
                    className="w-full flex items-start gap-3 p-3 rounded-xl border border-zinc-100 hover:border-zinc-200 text-left transition-colors"
                    data-testid="cookie-toggle-marketing"
                  >
                    <div className={`mt-0.5 w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${marketing ? "" : "bg-zinc-200"}`}
                         style={{ background: marketing ? ACCENT : undefined }}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-all ${marketing ? "ml-auto" : ""}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-zinc-900">Notifiche push & marketing</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Notifiche dal browser su offerte e novità (via OneSignal). Nessun retargeting pubblicitario.</p>
                    </div>
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 mt-4 justify-end flex-wrap">
                {!showDetails && (
                  <button
                    onClick={() => setShowDetails(true)}
                    className="mr-auto inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-zinc-600 hover:text-zinc-900 rounded-lg hover:bg-zinc-50 transition-colors"
                    data-testid="cookie-customize-btn"
                  >
                    <Sliders className="w-3.5 h-3.5" /> Personalizza
                  </button>
                )}
                <button
                  onClick={rejectAll}
                  className="px-4 py-2 text-xs font-semibold text-zinc-600 hover:text-zinc-900 rounded-lg hover:bg-zinc-50 transition-colors border border-zinc-200"
                  data-testid="cookie-reject-btn"
                >
                  Solo essenziali
                </button>
                {showDetails ? (
                  <button
                    onClick={saveGranular}
                    className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white rounded-xl transition-all hover:opacity-90"
                    style={{ background: ACCENT }}
                    data-testid="cookie-save-btn"
                  >
                    <Check className="w-3.5 h-3.5" /> Salva scelte
                  </button>
                ) : (
                  <button
                    onClick={acceptAll}
                    className="px-5 py-2 text-xs font-bold text-white rounded-xl transition-all hover:opacity-90"
                    style={{ background: ACCENT }}
                    data-testid="cookie-accept-btn"
                  >
                    Accetta tutti
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
